import { CommonModule } from '@angular/common'
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http'
import { ErrorHandler, inject, NgModule, provideAppInitializer } from '@angular/core'
import { JWT_OPTIONS, JwtModule } from '@auth0/angular-jwt'
import { API_CONFIGURATION, AUTH_CONFIGURATION } from '@ftr/api-shared'
import {
  ConfirmationModalComponent,
  DialogComponent,
  LayoutService,
  SimpleWindowRefService,
  SizesService,
  TrackingService,
} from '@ftr/foundation'
import { FeatureFlagState, LAUNCH_DARKLY_CONFIG, LaunchDarklyModule } from '@ftr/ui-feature-flags'
import { OBSERVABILITY_CONFIGURATION } from '@ftr/ui-observability'
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin'
import { NgxsModule } from '@ngxs/store'
/*
 * FTR Web App Imports
 */
import { RepositoryState } from '@ftr/api-recording-vault'
import { CourtSystemsState } from '@ftr/data-court-system'
import { CourtroomState } from '@ftr/ui-location'
import { RealTimeSttState } from '@ftr/ui-rtstt'
import { AnalyticsService, UserState } from '@ftr/ui-user'
import { VocabularyState } from '@ftr/ui-vocab'
import { LaunchDarklyAttributeService } from '~app/services/launch-darkly-attribute/launch-darkly-attribute.service'
/*
 * Platform and Environment providers/directives/pipes
 */
import { BreadcrumbModule, PageComponent, PortalComponent, ToastModule } from '@ftr/foundation'
import { ErrorHandlerService } from '@ftr/ui-observability'
import { AuthenticationService } from '@ftr/ui-user'
import { AppRoutingModule } from '~admin/app/app-routing.module'
import { CoreModule } from '~app/core/core.module'
import { ConfigurationService } from '~app/services/configuration/configuration.service'
import { AuthRedirectInterceptor } from '~app/services/http-client/auth-redirect.interceptor'
import { jwtOptionsFactory } from '~app/services/http-client/jwt-options.factory'
import { LoggingInterceptor } from '~app/services/http-client/logging.interceptor'
/*
 * FTR Admin App Imports
 */
import { DepartmentService } from '@ftr/api-core'
import { provideAngularSvgIcon, SvgIconComponent } from 'angular-svg-icon'
import { AppComponent } from '~admin/app/app.component'
import { HeaderModule } from '~admin/app/core/header/header.module'
import { GlobalAdminHeaderSizeService } from '~admin/app/services/header-size/global-admin-header-size.service'
import { NGXS_OPTIONS } from '~admin/app/store/ngxs-options'
// Application wide providers
const APP_PROVIDERS = [
  { provide: ErrorHandler, useClass: ErrorHandlerService },
  { provide: TrackingService, useClass: AnalyticsService },
  { provide: LayoutService, useValue: LayoutService },
  { provide: SimpleWindowRefService, useClass: SimpleWindowRefService },
  { provide: API_CONFIGURATION, useClass: ConfigurationService },
  { provide: LAUNCH_DARKLY_CONFIG, useClass: ConfigurationService },
  { provide: OBSERVABILITY_CONFIGURATION, useClass: ConfigurationService },
  { provide: AUTH_CONFIGURATION, useClass: ConfigurationService },
  { provide: DepartmentService, useClass: DepartmentService },
  { provide: SizesService, useClass: GlobalAdminHeaderSizeService },
  // Wire up launch darkly to apply attributes globally from the store
  provideAppInitializer(() => {
    const initializerFn = (
      (service: LaunchDarklyAttributeService) => () =>
        service.observeAndApplyUserAttributes()
    )(inject(LaunchDarklyAttributeService))
    return initializerFn()
  }),
  provideAngularSvgIcon(),
]
const httpInterceptorProviders = [
  {
    provide: HTTP_INTERCEPTORS,
    useClass: AuthRedirectInterceptor,
    multi: true,
  },
  { provide: HTTP_INTERCEPTORS, useClass: LoggingInterceptor, multi: true },
]
/**
 * `AppModule` is the main entry point into Angular's bootstrap process
 */
@NgModule({
  bootstrap: [AppComponent],
  declarations: [AppComponent],
  imports: [
    CommonModule,
    NgxsModule.forRoot(
      [
        UserState,
        CourtSystemsState,
        VocabularyState,
        RepositoryState,
        CourtroomState,
        FeatureFlagState,
        RealTimeSttState,
      ],
      NGXS_OPTIONS,
    ),
    HeaderModule,
    JwtModule.forRoot({
      jwtOptionsProvider: {
        provide: JWT_OPTIONS,
        useFactory: jwtOptionsFactory,
        deps: [AuthenticationService, ConfigurationService],
      },
    }),
    CoreModule.forRoot(),
    AppRoutingModule,
    NgxsReduxDevtoolsPluginModule.forRoot({
      disabled: true,
      name: 'FTR-ADMIN',
    }),
    BreadcrumbModule,
    ToastModule,
    DialogComponent,
    PortalComponent,
    PageComponent,
    LaunchDarklyModule.forRoot(),
    SvgIconComponent,
    ConfirmationModalComponent,
  ],
  providers: [APP_PROVIDERS, httpInterceptorProviders, provideHttpClient(withInterceptorsFromDi())],
})
export class AppModule {}
